/**
 * React hook to warn user before leaving page if there are unsaved changes
 */

import { useEffect } from 'react';

interface UseRefreshWarningProps {
  enabled: boolean;
}

let activeWarnings = 0;
let isUnloadAllowed = false;

/**
 * Whether anything on screen is holding changes the reader has not saved.
 *
 * An action that discards them without the browser's own prompt — one that has
 * already passed the point of no return by the time that prompt would appear —
 * asks first by reading this.
 */
export const hasUnsavedChanges = () => activeWarnings > 0;

/**
 * Lets the next unload through unprompted, for a departure the reader has
 * already agreed to. Nothing resets it: the document it permits is the one
 * being replaced.
 */
export const allowUnload = () => {
  isUnloadAllowed = true;
};

const useRefreshWarning = ({ enabled }: UseRefreshWarningProps) => {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isUnloadAllowed) {
        return;
      }

      event.preventDefault();
      event.returnValue = ''; // Required for Chrome
    };

    activeWarnings += 1;
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      activeWarnings -= 1;
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled]);
};

export default useRefreshWarning;
